import React, { useEffect, useState, useRef, useContext } from "react";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";

import { LabelTooltip } from "../../../components/coding/labelTooltip";
import { paymentContactTooltips } from "../../../config/tooltips/pageTooltips";

import braintree from "braintree-web";

import { PaymentContext } from "../../../context/BraintreeContext.jsx";
import useSharedData from "../../../hooks/useSharedData.ts";
import { useTheme } from "@mui/material/styles";

import { NotificationCard } from "@arp/react-ndbx";
import { useOutletContext } from "react-router-dom";

import "./style.css";

//----------------------------------------------------------------

const CreditDebitCard = () => {
	const paymentContext = useContext(PaymentContext);
	const {
		tokenObject,
		setAdditionalInformationData,
		setTransactionNonce,
		getToken,
		setFormValid,
	} = paymentContext;

	const { accessToken, handleEventChange } = useSharedData();
	const [clientAccessToken, setClientAccessToken] = useState(tokenObject);

	useEffect(() => {
		getToken(accessToken);
		setFormValid(false);
	}, [accessToken]);

	useEffect(() => {
		handleEventChange({ name: "isPageLoading", value: true });
	}, []);

	useEffect(() => {
		if (!tokenObject) return;
		setClientAccessToken(tokenObject.clientToken);

		setResultString("Token applied");
	}, [tokenObject]);

	const theme = useTheme();
	const [error, setError] = useState("");
	const [hostedFieldsInstance, setHostedFieldsInstance] = useState(null);
	const [resultString, setResultString] = useState("Waiting...");

	const {
		methods: [methods],
		showSecond: [showSecond],
		isDirty: [isDirty],
		isValid: [isValid],
	} = useOutletContext();

	const { resetField, setValue, trigger } = {
		...methods,
	};

	const handleFocus = (field) => {
		const label = document.querySelector(
			`.hosted-field--label[for="${field.container.id}"]`
		);

		label.classList.add("label-float");
		label.classList.remove("filled");
	};

	const handleBlur = (field) => {
		const label = document.querySelector(
			`.hosted-field--label[for="${field.container.id}"]`
		);
		const errorNotification = document.querySelector(
			`.hosted-field--notification[for="${field.container.id}"]`
		);

		if (field.isEmpty) {
			label.classList.remove("label-float");
			label.classList.add("invalid");
			errorNotification.classList.add("invalid");
		} else if (field.isValid) {
			label.classList.add("filled");
			errorNotification.classList.remove("invalid");
		} else {
			label.classList.add("invalid");
			errorNotification.classList.add("invalid");
		}
	};

	const handleEmpty = (field) => {
		const label = document.querySelector(
			`.hosted-field--label[for="${field.container.id}"]`
		);
		label.classList.remove("filled");
		label.classList.remove("invalid");
	};

	const handleValidityChange = (field) => {
		const label = document.querySelector(
			`.hosted-field--label[for="${field.container.id}"]`
		);

		if (field.isPotentiallyValid) {
			label.classList.remove("invalid");
		} else {
			label.classList.add("invalid");
		}
	};

	function isObjEmpty(obj) {
		return Object?.keys(obj)?.length === 0;
	}

	useEffect(() => {
		const initializeBraintree = () => {
			if (
				!clientAccessToken ||
				clientAccessToken == null ||
				isObjEmpty(clientAccessToken)
			)
				return;
			braintree.client.create(
				{
					authorization: clientAccessToken,
				},
				(err, clientInstance) => {
					if (err) {
						console.error("Error initializing Braintree:", err);
						setError("Error initializing Braintree. Please try again later.");
						return;
					}

					braintree.hostedFields.create(
						{
							client: clientInstance,
							styles: {
								input: {
									"font-size": "20px",
									"font-weight": 500,
									"font-family": theme.typography.fontFamily,
									color: "#414141",
								},
								":focus": {
									color: "#006192",
									"font-weight": 500,
								},
								".invalid": {
									color: "#414141",
									"font-weight": 500,
								},
							},
							fields: {
								cardholderName: {
									selector: "#cardholder-name",
								},
								number: {
									selector: "#card-number",
								},
								expirationDate: {
									selector: "#expiration-date",
								},
								cvv: {
									selector: "#cvv",
								},
							},
						},
						(err, hostedFieldsInstance) => {
							if (err) {
								console.error(err);
								return;
							}
							hostedFieldsInstance.on("focus", (event) => {
								const field = event.fields[event.emittedBy];
								handleFocus(field);
							});
							hostedFieldsInstance.on("blur", (event) => {
								const field = event.fields[event.emittedBy];

								handleFocus(field);
								handleBlur(field);
							});
							hostedFieldsInstance.on("empty", (event) => {
								const field = event.fields[event.emittedBy];
								handleEmpty(field);
							});

							hostedFieldsInstance.on("validityChange", (event) => {
								const field = event.fields[event.emittedBy];
								handleValidityChange(field);

								var formValid = Object.keys(event.fields).every(function (key) {
									return event.fields[key].isValid;
								});

								setFormValid(formValid);

								// The form is valid, we need to pass the Nonce to the context.
								hostedFieldsInstance.tokenize((err, payload) => {
									if (err) {
										console.error("Error tokenizing payment:", err);
										setError(
											"Error processing payment. Please try again later."
										);
										return;
									} else {
										setTransactionNonce(payload.nonce);

										//* We need to pass the Name on Card and Card Type to the API.
										var cardholderName = payload.details.cardholderName;
										var cardType = payload.details.cardType;

										const additionalData = {
											nameOnCard: cardholderName,
											cardType: cardType,
										};

										setAdditionalInformationData(additionalData);
									}
								});
							});
							handleEventChange({ name: "isPageLoading", value: false });
							setHostedFieldsInstance(hostedFieldsInstance);
						}
					);
				}
			);
		};
		initializeBraintree();
	}, [clientAccessToken]);

	const StyledInput = ({
		idRef,
		label,
		subLabel,
		showTooltip = false,
		tooltipText,
		controllerId,
		error = "",
	}) => {
		return (
			<Box
				className="textfield--float-label"
				mt={0}
				style={{ fontFamily: theme.typography.fontFamily }}
			>
				<label
					className="hosted-field--label"
					htmlFor={idRef}
					style={{ fontFamily: theme.typography.fontFamily }}
				>
					{label}
				</label>
				<Stack direction={"row"}>
					<Box className="hosted-field" id={idRef} name={idRef}></Box>
					{showTooltip ? (
						<LabelTooltip
							zIndex={100}
							controlIdentifier={controllerId}
							tooltipTitle={tooltipText}
						/>
					) : null}
				</Stack>
				{subLabel ? (
					<Stack>
						<Typography
							mt={2}
							sx={{ visibility: subLabel === "_" ? "hidden" : "visible" }}
							className="hosted-field--subLabel"
						>
							{subLabel}
						</Typography>
					</Stack>
				) : null}
				<Box
					className="hosted-field--notification"
					htmlFor={idRef}
					mt={subLabel ? 1 : 0}
					sx={{ width: "100%" }}
				>
					<NotificationCard
						cardType={"error"}
						title={""}
						message={error}
						sx={{ minWidth: "100%" }}
					/>
				</Box>
			</Box>
		);
	};

	return (
		<>
			<Paper elevation={0} sx={{ p: 0 }}>
				<Stack>
					{StyledInput({
						idRef: "cardholder-name",
						label: "Name on card",
						error: "Name on card is not valid",
					})}
					{StyledInput({
						idRef: "card-number",
						label: "Card number",
						error: "Card number is not valid",
					})}

					<Stack direction={{ xs: "column", sm: "row" }}>
						{StyledInput({
							idRef: "expiration-date",
							label: "Expiration date",
							subLabel: "MM/YY",
							error: "Expiration date is not valid",
						})}
						{StyledInput({
							idRef: "cvv",
							label: "Security code (CVV)",
							showTooltip: true,
							tooltipText: paymentContactTooltips.CCV_INFO_TOOLTIP,
							controllerId: "security-code-cvv-tooltip",
							error: "Security code is not valid",
							subLabel: "_",
						})}
					</Stack>
				</Stack>
				<br />
			</Paper>
		</>
	);
};

export default CreditDebitCard;
